import { createSpatialIndex } from './spatialIndex';

const DIRECTION_VECTORS = Object.freeze({
  up: Object.freeze({ x: 0, y: -1 }),
  down: Object.freeze({ x: 0, y: 1 }),
  left: Object.freeze({ x: -1, y: 0 }),
  right: Object.freeze({ x: 1, y: 0 }),
});

const DEFAULT_NODE_SIZE = Object.freeze({ width: 260, height: 110 });
const DIRECTION_CONE_TANGENT = Math.sqrt(3); // 60 degrees either side of the key direction

const finiteOr = (value, fallback) => (Number.isFinite(value) ? value : fallback);

const normalizeRect = (rect) => {
  if (!rect) return null;
  const x = finiteOr(rect.x, null);
  const y = finiteOr(rect.y, null);
  if (x === null || y === null) return null;
  return {
    x,
    y,
    width: Math.max(0, finiteOr(rect.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(0, finiteOr(rect.height, DEFAULT_NODE_SIZE.height)),
  };
};

export const getRectCenter = (rect) => {
  const safeRect = normalizeRect(rect);
  if (!safeRect) return null;
  return {
    x: safeRect.x + safeRect.width / 2,
    y: safeRect.y + safeRect.height / 2,
  };
};

const candidateScore = ({ origin, candidate, direction, stableOrder }) => {
  const vector = DIRECTION_VECTORS[direction];
  if (!vector) return null;
  const dx = candidate.x - origin.x;
  const dy = candidate.y - origin.y;
  const forward = dx * vector.x + dy * vector.y;
  if (forward <= 0) return null;
  const perpendicular = Math.abs(dx * vector.y - dy * vector.x);
  if (perpendicular > forward * DIRECTION_CONE_TANGENT) return null;
  return {
    distanceSquared: dx * dx + dy * dy,
    perpendicular,
    forward,
    stableOrder,
  };
};

const compareScores = (left, right) => {
  if (left.distanceSquared !== right.distanceSquared) {
    return left.distanceSquared - right.distanceSquared;
  }
  if (left.perpendicular !== right.perpendicular) {
    return left.perpendicular - right.perpendicular;
  }
  if (left.forward !== right.forward) return left.forward - right.forward;
  return left.stableOrder - right.stableOrder;
};

/**
 * Builds the immutable geometry used by directional keyboard navigation.
 * Candidate queries expand through the shared grid index, avoiding a full-map
 * scan or sort for every key press.
 */
export function createDirectionalNavigationIndex(items = [], {
  getId = (item) => item?.id,
  getRect = (item) => item?.rect || item,
  eligibleIds = null,
  cellSize,
} = {}) {
  const entries = [];
  const eligible = eligibleIds instanceof Set
    ? eligibleIds
    : new Set(eligibleIds || (Array.isArray(items) ? items.map(getId) : []));
  let minCenterX = Infinity;
  let minCenterY = Infinity;
  let maxCenterX = -Infinity;
  let maxCenterY = -Infinity;

  for (const item of Array.isArray(items) ? items : []) {
    const id = getId(item);
    const rect = normalizeRect(getRect(item));
    if (id === undefined || id === null || !rect) continue;
    // The rectangle is already normalized above. Avoid normalizing and
    // allocating it a second time for every idea in large-map index builds.
    const center = {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height / 2,
    };
    const entry = {
      id,
      item,
      rect,
      center,
      stableOrder: entries.length,
    };
    entries.push(entry);
    if (eligible.has(id)) {
      minCenterX = Math.min(minCenterX, center.x);
      minCenterY = Math.min(minCenterY, center.y);
      maxCenterX = Math.max(maxCenterX, center.x);
      maxCenterY = Math.max(maxCenterY, center.y);
    }
  }

  const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
  const spatialIndex = createSpatialIndex(entries, {
    getRect: (entry) => entry.rect,
    ...(cellSize ? { cellSize } : {}),
  });

  const find = (originId, direction, { originCenter = null } = {}) => {
    const originEntry = entriesById.get(originId);
    if (
      !originEntry
      || !eligible.has(originId)
      || !DIRECTION_VECTORS[direction]
      || eligible.size < 2
    ) return null;

    const origin = (
      Number.isFinite(originCenter?.x) && Number.isFinite(originCenter?.y)
    )
      ? { x: originCenter.x, y: originCenter.y }
      : originEntry.center;
    const maximumRadius = Math.max(
      spatialIndex.cellSize,
      Math.abs(origin.x - minCenterX),
      Math.abs(origin.x - maxCenterX),
      Math.abs(origin.y - minCenterY),
      Math.abs(origin.y - maxCenterY),
    );
    const seen = new Set([originId]);
    let radius = Math.min(spatialIndex.cellSize, maximumRadius);
    let best = null;

    while (radius <= maximumRadius) {
      const candidates = spatialIndex.query({
        x: origin.x - radius,
        y: origin.y - radius,
        width: radius * 2,
        height: radius * 2,
      });
      for (const candidate of candidates) {
        if (!eligible.has(candidate.id) || seen.has(candidate.id)) continue;
        seen.add(candidate.id);
        const score = candidateScore({
          origin,
          candidate: candidate.center,
          direction,
          stableOrder: candidate.stableOrder,
        });
        if (!score) continue;
        if (!best || compareScores(score, best.score) < 0) {
          best = { entry: candidate, score };
        }
      }

      if (best && best.score.distanceSquared <= radius * radius) break;
      if (radius === maximumRadius) break;
      radius = Math.min(maximumRadius, radius * 2);
    }

    if (!best) return null;
    return {
      id: best.entry.id,
      item: best.entry.item,
      originCenter: { ...origin },
      destinationCenter: { ...best.entry.center },
    };
  };

  return Object.freeze({
    size: entries.length,
    find,
    spatialIndex: Object.freeze({
      size: spatialIndex.size,
      cellSize: spatialIndex.cellSize,
      query: (rect, options) => spatialIndex
        .query(rect, options)
        .map((entry) => entry.item),
    }),
  });
}
